/**
 * Centralized Email API (via MyAMP proxy)
 * ---------------------------------------
 * The browser cannot call the NC Integrations API / MAC email API directly:
 *   • /api/Secret requires an `Origin: <agency nowcerts domain>` header, and
 *     browsers forbid JS from setting Origin.
 *   • Cross-origin CORS + a decrypted secret_key that must not be exposed client-side.
 *
 * So MyAMP proxies the whole flow server-to-server (fetch+decrypt the secret,
 * call the MAC email API) at {REACT_APP_MyAMP_Url}/api/centralized-email/*.
 *
 * The portal calls that proxy with:
 *   • Authorization: Bearer <amsToken>   (NowCerts token from localStorage)
 *   • X-Agency-Domain: <agencyDomain>    (agency NowCerts domain → used as Origin)
 */

const EMAIL_API = `${process.env.REACT_APP_MyAMP_Url}/api/centralized-email/integrations`;

export type EmailProvider = 'gmail' | 'exchange' | 'imap';
export type EmailIntegrationStatus = 'active' | 'expired' | 'revoked';
export type EmailSharingType = 'private' | 'agency_wide' | 'specific_agents';

export interface MacEmailIntegration {
  id: number;
  provider: EmailProvider;
  email_address: string;
  status: EmailIntegrationStatus;
  default: boolean;
  centralized: boolean;
  connected_from_app: string | null;
  last_used_at: string | null;
  created_at: string | null;
  is_owner: boolean;
  sharing_type: EmailSharingType;
  integration_purpose: string | null;
  // AI automation on/off for this integration. Field name may vary across the
  // API; read both `ai_automation_enabled` and `ai_automation`.
  ai_automation_enabled?: boolean;
  ai_automation?: boolean;
}

/** Momentum MAC runs AI automation only on its default integration; other purposes (e.g. support) are listed but can't be turned on. */
export function isAiSupportedIntegration(integration: MacEmailIntegration): boolean {
  const purpose = String(integration.integration_purpose ?? '').trim().toLowerCase();
  return purpose === '' || purpose === 'default_mac';
}

function authHeaders(): HeadersInit {
  // authToken (MyAMP/IA.AI) — carries userid/companyid; the primary Bearer.
  const authToken = localStorage.getItem('authToken') || '';
  const agencyDomain = localStorage.getItem('agencyDomain') || '';
  // amsToken (NowCerts) — needed server-side ONLY for the MAC /api/Secret call
  // (with Origin). Sent in a separate header so the Bearer stays the authToken.
  const amsToken = localStorage.getItem('amsToken') || '';
  return {
    Authorization: `Bearer ${authToken}`,
    'X-Agency-Domain': agencyDomain,
    'X-Ams-Token': amsToken,
    'Content-Type': 'application/json',
  };
}

/**
 * Thrown when Momentum email exchange isn't set up for the user/agency on the MAC side
 * (the MyAMP proxy returns 200 with `not_configured: true`). Callers can catch this
 * specifically to show setup guidance instead of a generic error toast.
 */
export class EmailExchangeNotConfiguredError extends Error {
  readonly notConfigured = true;
  constructor(message?: string) {
    super(
      message ||
        'Momentum email exchange is not configured for your account. ' +
          'Please contact your administrator to set up email integration.',
    );
    this.name = 'EmailExchangeNotConfiguredError';
  }
}

async function readJson<T>(res: Response, context: string): Promise<T> {
  if (!res.ok) {
    let msg = `${context} failed: ${res.status}`;
    try {
      const body = await res.json();
      const apiErr = body?.errors?.[0]?.message;
      if (apiErr) msg = apiErr;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const data = await res.json();
  // The proxy returns HTTP 200 with `not_configured: true` when the MAC side has no
  // email-exchange secret for this user/agency. Surface it as a typed error so the UI
  // can render a friendly "not configured" state rather than an empty/broken list.
  if (data && data.not_configured === true) {
    throw new EmailExchangeNotConfiguredError(data?.errors?.[0]?.message);
  }
  return data as T;
}

/**
 * Check whether the user has a connected, active email integration.
 * Pass providerPreference to filter (e.g. "gmail").
 */
export async function checkEmailIntegration(
  providerPreference?: EmailProvider,
): Promise<{
  hasIntegration: boolean;
  integration: MacEmailIntegration | null;
}> {
  const qs = providerPreference
    ? `?provider_preference=${encodeURIComponent(providerPreference)}`
    : '';
  const res = await fetch(`${EMAIL_API}/check${qs}`, {
    headers: authHeaders(),
  });
  const data = await readJson<{
    success: boolean;
    has_integration: boolean;
    integration?: MacEmailIntegration;
  }>(res, 'check email integration');
  return {
    hasIntegration: !!data.has_integration,
    integration: data.integration ?? null,
  };
}

/**
 * List all active email integrations visible to the user (own + shared).
 * Optionally filter to a single provider client-side.
 */
export async function listEmailIntegrations(
  provider?: EmailProvider,
): Promise<MacEmailIntegration[]> {
  const res = await fetch(EMAIL_API, { headers: authHeaders() });
  const data = await readJson<{
    success: boolean;
    integrations?: MacEmailIntegration[];
  }>(res, 'list email integrations');
  const all = Array.isArray(data.integrations) ? data.integrations : [];
  return provider ? all.filter((i) => i.provider === provider) : all;
}

/** Get a single integration by id. */
export async function getEmailIntegration(
  id: number,
): Promise<MacEmailIntegration | null> {
  const res = await fetch(`${EMAIL_API}/${id}`, { headers: authHeaders() });
  const data = await readJson<{
    success: boolean;
    integration?: MacEmailIntegration;
  }>(res, 'get email integration');
  return data.integration ?? null;
}

/** Enable/disable AI automation for an integration. */
export async function setEmailAiAutomation(
  id: number,
  enabled: boolean,
): Promise<void> {
  const res = await fetch(
    `${EMAIL_API}/${id}/ai_automation?enabled=${enabled ? 'true' : 'false'}`,
    { method: 'PUT', headers: authHeaders() },
  );
  await readJson<{ success: boolean; message?: string }>(
    res,
    'update AI automation',
  );
}
